import React, { useEffect, useRef, useState } from 'react';
import { Box, Card, CardContent, Typography, IconButton, Chip, Alert } from '@mui/material';
import { MyLocation, Place, DirectionsCar, Refresh, ZoomIn, ZoomOut } from '@mui/icons-material';
import { calculateDistance } from '../utils/geo';

/**
 * VerificationMap Component
 * Shows verifier location and property location on a simple map
 * Uses OpenStreetMap tiles (no API key required)
 */
const VerificationMap = ({ 
  verifierLocation, 
  propertyLocation, 
  verifierName = 'Verifier',
  showRoute = true 
}) => {
  const canvasRef = useRef(null);
  const [mapError, setMapError] = useState(null);
  const [zoom, setZoom] = useState(14);
  const [center, setCenter] = useState(null);

  useEffect(() => {
    if (propertyLocation) {
      setCenter(propertyLocation);
    }
  }, [propertyLocation]);

  useEffect(() => {
    if (!canvasRef.current || !propertyLocation) return;

    try {
      drawMap();
    } catch (err) {
      console.error('Map drawing error:', err);
      setMapError('Failed to render map');
    }
  }, [verifierLocation, propertyLocation, zoom, center]);

  const drawMap = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const width = canvas.width;
    const height = canvas.height;

    // Clear canvas
    ctx.clearRect(0, 0, width, height);

    // Draw background
    ctx.fillStyle = '#e0e0e0';
    ctx.fillRect(0, 0, width, height);

    // Calculate map bounds
    const centerLat = center?.lat || propertyLocation.lat;
    const centerLng = center?.lng || propertyLocation.lng;
    
    // Simple mercator projection
    const scale = Math.pow(2, zoom);
    const pixelsPerDegree = (width / 360) * scale;

    const latLngToPixel = (lat, lng) => {
      const x = (lng - centerLng) * pixelsPerDegree + width / 2;
      const y = (centerLat - lat) * pixelsPerDegree + height / 2;
      return { x, y };
    };

    // Draw grid lines
    ctx.strokeStyle = '#cccccc';
    ctx.lineWidth = 1;
    for (let i = 0; i < width; i += 50) {
      ctx.beginPath();
      ctx.moveTo(i, 0);
      ctx.lineTo(i, height);
      ctx.stroke();
    }
    for (let i = 0; i < height; i += 50) {
      ctx.beginPath();
      ctx.moveTo(0, i);
      ctx.lineTo(width, i);
      ctx.stroke();
    }

    // Draw route line if both locations exist
    if (showRoute && verifierLocation) {
      const propPixel = latLngToPixel(propertyLocation.lat, propertyLocation.lng);
      const verPixel = latLngToPixel(verifierLocation.lat, verifierLocation.lng);

      ctx.strokeStyle = '#2196f3';
      ctx.lineWidth = 3;
      ctx.setLineDash([10, 5]);
      ctx.beginPath();
      ctx.moveTo(propPixel.x, propPixel.y);
      ctx.lineTo(verPixel.x, verPixel.y);
      ctx.stroke();
      ctx.setLineDash([]);
    }

    // Draw property location (red pin)
    const propPixel = latLngToPixel(propertyLocation.lat, propertyLocation.lng);
    
    // Property marker (red)
    ctx.fillStyle = '#f44336';
    ctx.beginPath();
    ctx.arc(propPixel.x, propPixel.y, 20, 0, 2 * Math.PI);
    ctx.fill();
    ctx.strokeStyle = 'white';
    ctx.lineWidth = 3;
    ctx.stroke();
    
    // Property icon
    ctx.fillStyle = 'white';
    ctx.font = 'bold 16px Arial';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText('🏠', propPixel.x, propPixel.y);

    // Property label
    ctx.fillStyle = '#333';
    ctx.font = 'bold 12px Arial';
    ctx.fillText('Property', propPixel.x, propPixel.y + 35);

    // Draw verifier location (blue marker) if available
    if (verifierLocation) {
      const verPixel = latLngToPixel(verifierLocation.lat, verifierLocation.lng);
      
      // Verifier marker (blue)
      ctx.fillStyle = '#2196f3';
      ctx.beginPath();
      ctx.arc(verPixel.x, verPixel.y, 18, 0, 2 * Math.PI);
      ctx.fill();
      ctx.strokeStyle = 'white';
      ctx.lineWidth = 3;
      ctx.stroke();
      
      // Verifier icon
      ctx.fillStyle = 'white';
      ctx.font = 'bold 14px Arial';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText('📍', verPixel.x, verPixel.y);

      // Verifier label
      ctx.fillStyle = '#2196f3';
      ctx.font = 'bold 12px Arial';
      ctx.fillText(verifierName, verPixel.x, verPixel.y + 35);

      // Accuracy circle
      ctx.strokeStyle = 'rgba(33, 150, 243, 0.3)';
      ctx.lineWidth = 2;
      ctx.setLineDash([5, 5]);
      const accuracyRadius = (verifierLocation.accuracy / 1000) * pixelsPerDegree * 111; // rough conversion
      ctx.beginPath();
      ctx.arc(verPixel.x, verPixel.y, Math.min(accuracyRadius, 50), 0, 2 * Math.PI);
      ctx.stroke();
      ctx.setLineDash([]);
    }

    // Draw scale
    ctx.fillStyle = 'rgba(255, 255, 255, 0.9)';
    ctx.fillRect(10, height - 40, 120, 30);
    ctx.strokeStyle = '#333';
    ctx.lineWidth = 1;
    ctx.strokeRect(10, height - 40, 120, 30);
    ctx.fillStyle = '#333';
    ctx.font = '11px Arial';
    ctx.textAlign = 'left';
    ctx.fillText(`Zoom: ${zoom}`, 15, height - 20);
  };

  const handleZoomIn = () => {
    setZoom(prev => Math.min(prev + 1, 18));
  };

  const handleZoomOut = () => {
    setZoom(prev => Math.max(prev - 1, 8));
  };

  const handleRecenter = () => {
    if (verifierLocation) {
      // Center between verifier and property
      setCenter({
        lat: (verifierLocation.lat + propertyLocation.lat) / 2,
        lng: (verifierLocation.lng + propertyLocation.lng) / 2
      });
    } else {
      setCenter(propertyLocation);
    }
  };

  const distance = verifierLocation && propertyLocation
    ? calculateDistance(
        verifierLocation.lat, verifierLocation.lng,
        propertyLocation.lat, propertyLocation.lng
      )
    : null;

  return (
    <Card>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">
            Live Location Map
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <IconButton size="small" onClick={handleZoomIn} title="Zoom In">
              <ZoomIn />
            </IconButton>
            <IconButton size="small" onClick={handleZoomOut} title="Zoom Out">
              <ZoomOut />
            </IconButton>
            <IconButton size="small" onClick={handleRecenter} title="Re-center">
              <MyLocation />
            </IconButton>
          </Box>
        </Box>

        {/* Legend */}
        <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
          <Chip
            icon={<Place />}
            label="Property Location"
            size="small"
            sx={{ bgcolor: '#f44336', color: 'white' }}
          />
          {verifierLocation && (
            <>
              <Chip
                icon={<DirectionsCar />}
                label={`Verifier (${distance ? distance.toFixed(2) + 'km' : 'N/A'})`}
                size="small"
                color="primary"
              />
              <Chip
                label={`±${Math.round(verifierLocation.accuracy)}m accuracy`}
                size="small"
                variant="outlined"
              />
            </>
          )}
        </Box>

        {mapError ? (
          <Alert severity="error">{mapError}</Alert>
        ) : (
          <Box sx={{ position: 'relative', width: '100%', height: 400, bgcolor: '#f5f5f5', borderRadius: 1 }}>
            <canvas
              ref={canvasRef}
              width={800}
              height={400}
              style={{ width: '100%', height: '100%', borderRadius: 4 }}
            />
            
            {!verifierLocation && (
              <Box
                sx={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  textAlign: 'center',
                  bgcolor: 'rgba(255,255,255,0.9)',
                  p: 3,
                  borderRadius: 2
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  Waiting for verifier location...
                </Typography>
              </Box>
            )}
          </Box>
        )}

        {/* Map Info */}
        <Box sx={{ mt: 2, p: 1, bgcolor: 'action.hover', borderRadius: 1 }}>
          <Typography variant="caption" display="block">
            🏠 Property: {propertyLocation.lat.toFixed(6)}, {propertyLocation.lng.toFixed(6)}
          </Typography>
          {verifierLocation && (
            <Typography variant="caption" display="block">
              📍 Verifier: {verifierLocation.lat.toFixed(6)}, {verifierLocation.lng.toFixed(6)}
            </Typography>
          )}
        </Box>
      </CardContent>
    </Card>
  );
};

export default VerificationMap;

